import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  ExternalLink,
  FileText,
  Link as LinkIcon,
  Loader2,
  MapPin,
  Send,
} from "lucide-react";
import { API } from "service/Interceptor";
import { serverFiles } from "utils/utils";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const STATUS_LABEL = {
  PENDING: "pending",
  SHORTLIST: "shortlisted",
  SELECTED: "selected",
  REJECTED: "rejected",
};

const STATUS_STYLE = {
  pending: "status-pill status-pill-pending",
  shortlisted: "status-pill status-pill-shortlisted",
  selected: "status-pill status-pill-selected",
  rejected: "status-pill status-pill-rejected",
};

const FILTERS = ["All", "pending", "shortlisted", "selected", "rejected"];

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const BrandAvatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="h-11 w-11 rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div className="avatar-fallback h-11 w-11 rounded-full shrink-0 flex items-center justify-center font-semibold text-sm">
      {initials(name)}
    </div>
  );

const fieldFromDescription = (content, label) => {
  if (!content) return null;
  const re = new RegExp(`${label}:\\s*([^\\n]+)`, "i");
  return String(content).match(re)?.[1]?.trim() || null;
};

const displayCity = (a) => {
  const fromDesc = fieldFromDescription(
    a?.description || a?.jobResponseDto?.description,
    "City"
  );
  const raw =
    fromDesc ||
    a?.cityName ||
    a?.jobResponseDto?.cityName ||
    a?.city?.name ||
    a?.jobResponseDto?.city?.name ||
    null;
  if (!raw) return null;
  const label = String(raw).split(",")[0].trim();
  if (/^all\s*cities$/i.test(label)) return "All cities";
  return label || null;
};

const normalizeStatus = (raw) => {
  const key = String(raw || "PENDING").toUpperCase();
  return STATUS_LABEL[key] || "pending";
};

const MyApplicationsPage = () => {
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await API.get("api/job-applications");
        if (!alive) return;
        setApps(res?.data?.data?.results || []);
      } catch (_) {
        if (alive) setApps([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const counts = useMemo(() => {
    const c = { All: apps?.length || 0 };
    (apps || []).forEach((a) => {
      const s = normalizeStatus(a?.applicationStatus);
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [apps]);

  const visible = (apps || []).filter((a) => {
    if (filter === "All") return true;
    return normalizeStatus(a?.applicationStatus) === filter;
  });

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
          Your submissions
        </div>
        <h1 className="font-display font-bold text-4xl tracking-tighter text-zinc-50">
          My applications
        </h1>
        <p className="mt-2 text-zinc-400">
          Briefs you've applied to and where they stand.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
              filter === f
                ? "border-[#6C63F5] bg-[#6C63F5]/15 text-zinc-50"
                : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-600"
            }`}
          >
            <span className="capitalize">{f === "All" ? "All" : f}</span>
            <span className="text-[10px] text-zinc-500">{counts[f] || 0}</span>
          </button>
        ))}
      </div>

      {apps === null ? (
        <div className="py-20 text-center text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 py-20 text-center text-zinc-500">
          {apps.length === 0 ? (
            <>
              You haven't applied to any brief yet.{" "}
              <Link
                to="/projects"
                className="text-[#8b83ff] hover:text-white font-medium underline-offset-2 hover:underline"
              >
                Browse open briefs
              </Link>
            </>
          ) : (
            <>No applications with status "{filter}".</>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((a, i) => {
            const status = normalizeStatus(a?.applicationStatus);
            const brand =
              a?.company?.name ||
              a?.companyJobRespDto?.name ||
              a?.jobResponseDto?.company?.name ||
              [a?.company?.firstName, a?.company?.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              "Creator";
            const logoPath =
              a?.company?.logo ||
              a?.company?.profileUrl ||
              a?.companyJobRespDto?.logo ||
              a?.jobResponseDto?.company?.logo;
            const title =
              a?.jobTitle || a?.jobResponseDto?.jobTitle || "Brief";
            const industry =
              (Array.isArray(a?.industry) && a.industry[0]) ||
              (Array.isArray(a?.jobResponseDto?.industry) &&
                a.jobResponseDto.industry[0]) ||
              a?.category?.name;
            const city = displayCity(a);
            const creativeType = Array.isArray(a?.skillsRequired)
              ? a.skillsRequired[0]
              : Array.isArray(a?.jobResponseDto?.skillsRequired)
                ? a.jobResponseDto.skillsRequired[0]
                : null;
            const budget = formatBudget(
              a?.projectBudget ?? a?.jobResponseDto?.projectBudget
            );
            const timeline =
              a?.projectDuration || a?.jobResponseDto?.projectDuration;
            const appliedAt =
              a?.appliedAt ||
              a?.appliedDate ||
              a?.createdAt ||
              a?.applicationDate;
            const appId = a?.applicationNumber || a?.jobId || i;
            const note = a?.note || a?.coverLetter;
            const workLinks = a?.workLinks || a?.additionalLink;
            const portfolio =
              a?.portfolioUrl || a?.portfolioURL || a?.resumeUrl;

            return (
              <motion.div
                key={appId}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5"
              >
                <div className="flex items-start gap-3">
                  <BrandAvatar
                    src={logoPath ? serverFiles(logoPath) : null}
                    name={brand}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-zinc-50">
                        {brand}
                      </span>
                      {industry && (
                        <span className="rounded-full bg-[#6C63F5]/10 border border-[#6C63F5]/25 text-[#8b83ff] text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5">
                          {String(industry).split(",")[0]}
                        </span>
                      )}
                      <span
                        className={`text-[0.65rem] font-semibold uppercase tracking-wider rounded-full border px-2 py-0.5 ${
                          STATUS_STYLE[status] || STATUS_STYLE.pending
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                    <p className="mt-2 text-zinc-200 leading-relaxed">
                      {title}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                      {creativeType && (
                        <span>
                          Looking for{" "}
                          <span className="text-zinc-300">{creativeType}</span>
                        </span>
                      )}
                      {city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {city}
                        </span>
                      )}
                      {budget && (
                        <span>
                          Budget <span className="text-zinc-300">{budget}</span>
                        </span>
                      )}
                      {timeline && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} /> {timeline}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 ml-auto">
                        <Send size={11} /> Applied {timeAgo(appliedAt)}
                      </span>
                    </div>

                    {(note || workLinks || portfolio) && (
                      <div className="mt-3 border-t border-zinc-800/70 pt-3 space-y-2 text-sm">
                        {note && <p className="text-zinc-300">{note}</p>}
                        {workLinks && (
                          <div className="text-xs text-zinc-400 inline-flex items-start gap-1.5">
                            <LinkIcon
                              size={12}
                              className="text-[#8b83ff] mt-0.5"
                            />
                            <span className="text-zinc-200 break-all">
                              {Array.isArray(workLinks)
                                ? workLinks.join(", ")
                                : workLinks}
                            </span>
                          </div>
                        )}
                        {portfolio && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-[#8b83ff]">
                            <FileText size={12} /> Portfolio attached{" "}
                            <ExternalLink size={11} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyApplicationsPage;
